import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, BarChart3, AlertTriangle, TrendingUp, Maximize2, Minimize2, RotateCcw } from 'lucide-react';
import { MdCandlestickChart, MdShowChart } from 'react-icons/md';
import Layout from '../components/Layout';
import {
  createChart,
  createSeriesMarkers,
  ColorType,
  LineStyle,
  LineSeries,
  CandlestickSeries,
} from 'lightweight-charts';

// ── Zone thresholds & colors (matching app) ──
const BULLISH_THRESHOLD = 71;
const BEARISH_THRESHOLD = 30;

function getZoneColor(score) {
  if (score == null) return '#6b7280';
  if (score <= 10) return '#800000';
  if (score <= 30) return '#ef4444';
  if (score < 71) return '#6b7280';
  if (score < 91) return '#22c55e';
  return '#00FF66';
}

const SIGNAL_COLORS = {
  BUY: '#39ff14',
  BUY_LEVERAGED: '#00e5ff',
  SELL: '#ff2d55',
  SHORT_ENTRY: '#ff8c1a',
  SHORT_ACTIVE: '#fb923c',
  SHORT_EXIT: '#fbbf24',
};

// ── Parse CSV ──
function parseCSV(text) {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return { headers: [], rows: [], error: 'File must have at least a header and one data row' };
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const vals = lines[i].split(',').map(v => v.trim().replace(/['"]/g, ''));
    if (vals.length < 2) continue; // skip truly empty lines
    const row = {};
    headers.forEach((h, j) => { row[h] = j < vals.length ? vals[j] : ''; });
    rows.push(row);
  }
  return { headers, rows, error: null };
}

// ── Normalize date to yyyy-mm-dd ──
function normalizeDate(d) {
  if (!d) return null;
  d = d.trim();
  // Already yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  // dd-mm-yyyy or dd/mm/yyyy
  const m1 = d.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (m1) return `${m1[3]}-${m1[2]}-${m1[1]}`;
  // mm/dd/yyyy
  const m2 = d.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m2) return `${m2[3]}-${m2[1].padStart(2, '0')}-${m2[2].padStart(2, '0')}`;
  return d; // return as-is, will fail validation downstream
}

// ── Validate OHLC data ──
function validateOHLC(rows) {
  const required = ['date', 'close'];
  const first = rows[0];
  const missing = required.filter(f => !(f in first));
  if (missing.length) return `Missing columns: ${missing.join(', ')}. Required: date, open, high, low, close, score`;

  const parsed = rows.map(r => ({
    date: normalizeDate(r.date),
    open: r.open ? parseFloat(r.open) : null,
    high: r.high ? parseFloat(r.high) : null,
    low: r.low ? parseFloat(r.low) : null,
    close: parseFloat(r.close),
    score: r.score != null && r.score !== '' ? parseInt(r.score) : null,
    short_signal: r.short_signal || null,
  })).filter(r => !isNaN(r.close) && r.date);

  if (parsed.length === 0) return 'No valid data rows found';
  return parsed;
}

// ── Validate transactions ──
function validateTransactions(rows) {
  const first = rows[0];
  // Support both API format (type, date, price) and Excel format (trade #, buy date, sell date, buy price, sell price)
  if ('type' in first && 'date' in first) {
    // API format: trade_number, type, date, price, gain, days_held
    return rows.map(r => ({
      trade_number: parseInt(r.trade_number || r['trade #'] || 0),
      type: r.type.toUpperCase(),
      date: normalizeDate(r.date),
      price: parseFloat(r.price),
      gain: r.gain != null && r.gain !== '' ? parseFloat(r.gain) : null,
      days_held: r.days_held != null && r.days_held !== '' ? parseInt(r.days_held) : null,
    })).filter(r => r.date && !isNaN(r.price));
  } else if ('direction' in first && ('entry date' in first || 'entry_date' in first)) {
    // Admin export format: Trade #, Direction, Entry Date, Exit Date, Entry Price, Exit Price, Gain %
    const txns = [];
    rows.forEach(r => {
      const num = parseInt(r['trade #'] || r.trade_number || 0);
      const dir = (r.direction || 'LONG').toUpperCase();
      const entryDate = normalizeDate(r['entry date'] || r.entry_date);
      const exitDate = normalizeDate(r['exit date'] || r.exit_date);
      const entryPrice = parseFloat(r['entry price'] || r.entry_price);
      const exitPrice = parseFloat(r['exit price'] || r.exit_price);
      const gainStr = r['gain %'] || r.gain_pct || r.gain || '';
      const gain = gainStr ? parseFloat(gainStr.replace(/[+%]/g, '')) : null;
      const days = r['days held'] || r.days_held;
      const mae = r['mae %'] || r.mae;
      const entryType = dir === 'SHORT' ? 'SHORT SELL' : 'BUY';
      const exitType = dir === 'SHORT' ? 'SHORT COVER' : 'SELL';
      if (entryDate && !isNaN(entryPrice)) {
        txns.push({ trade_number: num, type: entryType, date: entryDate, price: entryPrice, gain: null, days_held: null });
      }
      if (exitDate && exitDate !== 'OPEN' && !isNaN(exitPrice)) {
        txns.push({ trade_number: num, type: exitType, date: exitDate, price: exitPrice, gain, days_held: days ? parseInt(days) : null });
      }
    });
    return txns;
  } else if ('buy date' in first || 'buy_date' in first) {
    // Legacy format: Trade #, BUY Date, SELL Date, BUY Price, SELL Price
    const txns = [];
    rows.forEach(r => {
      const num = parseInt(r['trade #'] || r.trade_number || 0);
      const buyDate = normalizeDate(r['buy date'] || r.buy_date);
      const sellDate = normalizeDate(r['sell date'] || r.sell_date);
      const buyPrice = parseFloat(r['buy price'] || r.buy_price);
      const sellPrice = parseFloat(r['sell price'] || r.sell_price);
      const gain = r['gain %'] || r.gain_pct || r.gain;
      if (buyDate && !isNaN(buyPrice)) {
        txns.push({ trade_number: num, type: 'BUY', date: buyDate, price: buyPrice, gain: null, days_held: null });
      }
      if (sellDate && sellDate !== 'OPEN' && !isNaN(sellPrice)) {
        txns.push({ trade_number: num, type: 'SELL', date: sellDate, price: sellPrice, gain: gain ? parseFloat(gain.toString().replace(/[+%]/g, '')) : null, days_held: r.days_held ? parseInt(r.days_held) : null });
      }
    });
    return txns;
  }
  return 'Unrecognized format. Expected columns: Direction, Entry Date, Exit Date OR type, date, price';
}

// ── Detect signals from transactions ──
function buildSignals(transactions, includeLongs = true, includeShorts = true) {
  const signals = [];
  for (const t of transactions) {
    const type = t.type.toUpperCase().trim();
    if (includeLongs && type === 'BUY') {
      signals.push({ time: t.date, position: 'belowBar', color: SIGNAL_COLORS.BUY, shape: 'arrowUp', text: 'BUY' });
    } else if (includeLongs && (type === 'SELL' || type === 'SELL (FORCED)')) {
      signals.push({ time: t.date, position: 'aboveBar', color: SIGNAL_COLORS.SELL, shape: 'arrowDown', text: 'SELL' });
    } else if (includeShorts && type === 'SHORT SELL') {
      signals.push({ time: t.date, position: 'aboveBar', color: SIGNAL_COLORS.SHORT_ENTRY, shape: 'arrowDown', text: 'SHORT' });
    } else if (includeShorts && (type === 'SHORT COVER' || type === 'SHORT COVER (FORCED)')) {
      signals.push({ time: t.date, position: 'belowBar', color: SIGNAL_COLORS.SHORT_EXIT, shape: 'arrowUp', text: 'COVER' });
    }
  }
  return signals;
}

// ── Compute stats from transactions (matching All Indexes format) ──
function computeStats(transactions) {
  // Pair entries with exits
  const sorted = [...transactions].sort((a, b) => a.date.localeCompare(b.date));
  const exits = sorted.filter(t => {
    const type = t.type.toUpperCase().trim();
    return ['SELL', 'SELL (FORCED)', 'SHORT COVER', 'SHORT COVER (FORCED)'].includes(type);
  });
  if (exits.length === 0) return null;

  const gains = exits.filter(t => t.gain != null).map(t => t.gain);
  if (gains.length === 0) return null;

  const wins = gains.filter(g => g > 0);
  const losses = gains.filter(g => g <= 0);
  const totalTrades = gains.length;
  const winRate = wins.length / totalTrades * 100;
  const avgReturn = gains.reduce((a, b) => a + b, 0) / totalTrades;
  const avgGain = wins.length > 0 ? wins.reduce((a, b) => a + b, 0) / wins.length : 0;
  const avgLoss = losses.length > 0 ? losses.reduce((a, b) => a + b, 0) / losses.length : 0;
  const bestGain = Math.max(...gains);
  const worstLoss = Math.min(...gains);
  const winLossRatio = avgLoss !== 0 ? Math.abs(avgGain / avgLoss) : 0;
  const expRatio = avgLoss !== 0 ? avgReturn / Math.abs(avgLoss) : 0;

  // Days held
  const daysArr = exits.filter(t => t.days_held != null).map(t => t.days_held);
  const avgDays = daysArr.length > 0 ? Math.round(daysArr.reduce((a, b) => a + b, 0) / daysArr.length) : 0;
  const totalDays = daysArr.reduce((a, b) => a + b, 0);

  // CAGR from first entry to last exit
  const entries = sorted.filter(t => ['BUY', 'SHORT SELL'].includes(t.type.toUpperCase().trim()));
  const firstDate = entries.length > 0 ? entries[0].date : sorted[0].date;
  const lastDate = exits.length > 0 ? exits[exits.length - 1].date : sorted[sorted.length - 1].date;
  const years = (new Date(lastDate) - new Date(firstDate)) / (365.25 * 86400000);

  let mult = 1;
  gains.forEach(g => { mult *= (1 + g / 100); });
  const cagr = years > 0.1 ? (Math.pow(mult, 1 / years) - 1) * 100 : 0;
  const tradesPerYear = years > 0.1 ? totalTrades / years : totalTrades;

  // Time in market
  const totalCalendarDays = years * 365.25;
  const timeInMarket = totalCalendarDays > 0 ? (totalDays / totalCalendarDays) * 100 : 0;

  // Shorts breakdown
  const shortExits = exits.filter(t => t.type.toUpperCase().includes('SHORT'));
  const longExits = exits.filter(t => !t.type.toUpperCase().includes('SHORT'));

  return {
    cagr: cagr.toFixed(2),
    tradesPerYear: tradesPerYear.toFixed(1),
    avgDays,
    timeInMarket: Math.round(timeInMarket),
    totalTrades,
    wins: wins.length,
    losses: losses.length,
    winRate: winRate.toFixed(1),
    avgReturn: avgReturn.toFixed(2),
    avgGain: avgGain.toFixed(2),
    avgLoss: avgLoss.toFixed(2),
    winLossRatio: winLossRatio.toFixed(2),
    expRatio: expRatio.toFixed(2),
    bestGain: bestGain.toFixed(2),
    worstLoss: worstLoss.toFixed(2),
    years: years.toFixed(1),
    longTrades: longExits.length,
    shortTrades: shortExits.length,
  };
}

function computeYearlyStats(transactions) {
  const sorted = [...transactions].sort((a, b) => a.date.localeCompare(b.date));
  const exits = sorted.filter(t => ['SELL', 'SELL (FORCED)', 'SHORT COVER', 'SHORT COVER (FORCED)'].includes(t.type.toUpperCase().trim()));
  if (exits.length === 0) return [];

  const byYear = {};
  exits.forEach(t => {
    const yr = parseInt(t.date.substring(0, 4));
    if (!byYear[yr]) byYear[yr] = [];
    byYear[yr].push(t);
  });

  return Object.keys(byYear).sort((a, b) => b - a).map(yr => {
    const trades = byYear[yr];
    const gains = trades.filter(t => t.gain != null).map(t => t.gain);
    const wins = gains.filter(g => g > 0);
    const losses = gains.filter(g => g <= 0);
    let mult = 1;
    gains.forEach(g => { mult *= (1 + g / 100); });
    const stratReturn = (mult - 1) * 100;
    const daysArr = trades.filter(t => t.days_held != null).map(t => t.days_held);
    const totalDays = daysArr.reduce((a, b) => a + b, 0);
    const avgDays = daysArr.length > 0 ? Math.round(totalDays / daysArr.length) : 0;
    const maes = trades.filter(t => t.mae != null).map(t => t.mae);
    const isShort = t => t.type.toUpperCase().includes('SHORT');
    return {
      year: parseInt(yr),
      total_trades: gains.length,
      long_trades: trades.filter(t => !isShort(t)).length,
      short_trades: trades.filter(t => isShort(t)).length,
      winning_trades: wins.length,
      losing_trades: losses.length,
      win_rate: gains.length > 0 ? (wins.length / gains.length * 100).toFixed(1) : '0',
      strategy_return: stratReturn.toFixed(2),
      avg_days: avgDays,
      time_in_market: Math.round(totalDays / 365 * 100),
      avg_mae: maes.length > 0 ? (maes.reduce((a, b) => a + b, 0) / maes.length).toFixed(2) : null,
      max_mae: maes.length > 0 ? Math.max(...maes).toFixed(2) : null,
    };
  });
}

// ── File upload component ──
function FileUpload({ label, hint, onData, error }) {
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState('');

  const handleFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const { headers, rows, error: parseError } = parseCSV(text);
      if (parseError) { onData(null, parseError); return; }
      onData({ headers, rows }, null);
    };
    reader.readAsText(file);
  };

  return (
    <div className="bg-dark-800 border border-dark-600 rounded-xl p-5">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-white font-semibold text-sm">{label}</h3>
          <p className="text-dark-400 text-xs mt-0.5">{hint}</p>
        </div>
        {fileName && <span className="text-xs text-dark-300 bg-dark-700 px-2 py-1 rounded">{fileName}</span>}
      </div>
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${error ? 'border-red-500/50 bg-red-900/10' : 'border-dark-500 hover:border-primary-500 hover:bg-dark-700/50'}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
      >
        <Upload className="w-6 h-6 text-dark-400 mx-auto mb-2" />
        <p className="text-dark-300 text-sm">Drop CSV file or click to browse</p>
        <input ref={inputRef} type="file" accept=".csv,.txt" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
      </div>
      {error && (
        <div className="mt-2 flex items-center gap-2 text-red-400 text-xs">
          <AlertTriangle className="w-3.5 h-3.5" />
          {error}
        </div>
      )}
    </div>
  );
}

// ── Zoom-responsive arrow sizing ──
function sizeForZoom(visibleBars) {
  if (visibleBars < 60) return 4;
  if (visibleBars < 150) return 3.5;
  if (visibleBars < 300) return 2.5;
  if (visibleBars < 600) return 2;
  return 1.5;
}

function applySize(signals, size) {
  return signals.map(s => ({ ...s, size }));
}

// ── Main component ──
export default function ChartSimulator() {
  const chartRef = useRef(null);
  const chartContainerRef = useRef(null);
  const chartApiRef = useRef(null);
  const markersPluginRef = useRef(null);
  const signalsRef = useRef([]);
  const [ohlcData, setOhlcData] = useState(null);
  const [txnData, setTxnData] = useState(null);
  const [ohlcError, setOhlcError] = useState('');
  const [txnError, setTxnError] = useState('');
  const [chartType, setChartType] = useState('line');
  const [showSignals, setShowSignals] = useState(true);
  const showSignalsRef = useRef(true);
  const [showLongs, setShowLongs] = useState(true);
  const showLongsRef = useRef(true);
  const [showShorts, setShowShorts] = useState(true);
  const showShortsRef = useRef(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [stats, setStats] = useState(null);
  const [yearlyStats, setYearlyStats] = useState([]);
  const [simTab, setSimTab] = useState('yearly');
  const [selectedYear, setSelectedYear] = useState('');
  const [simFilterType, setSimFilterType] = useState('all');

  const handleOHLC = useCallback(({ headers, rows } = {}, error) => {
    if (error) { setOhlcError(error); setOhlcData(null); return; }
    const result = validateOHLC(rows);
    if (typeof result === 'string') { setOhlcError(result); setOhlcData(null); return; }
    setOhlcError('');
    setOhlcData(result);
  }, []);

  // Fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!chartContainerRef.current) return;
    if (!document.fullscreenElement) {
      chartContainerRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      if (!fs && chartApiRef.current && chartRef.current) {
        setTimeout(() => {
          chartApiRef.current?.applyOptions({ width: chartRef.current.clientWidth, height: 500 });
          chartApiRef.current?.timeScale().fitContent();
        }, 50);
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const handleTxn = useCallback(({ headers, rows } = {}, error) => {
    if (error) { setTxnError(error); setTxnData(null); return; }
    const result = validateTransactions(rows);
    if (typeof result === 'string') { setTxnError(result); setTxnData(null); return; }
    setTxnError('');
    setTxnData(result);
    setStats(computeStats(result));
    setYearlyStats(computeYearlyStats(result));
  }, []);

  // Render chart when data changes
  useEffect(() => {
    if (!chartRef.current || !ohlcData || ohlcData.length === 0) return;

    if (chartApiRef.current) {
      chartApiRef.current.remove();
      chartApiRef.current = null;
    }

    const chart = createChart(chartRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#0f172a' },
        textColor: '#64748b',
        fontFamily: "'Inter', system-ui, sans-serif",
      },
      grid: {
        vertLines: { color: 'rgba(51, 65, 85, 0.25)' },
        horzLines: { color: 'rgba(51, 65, 85, 0.25)' },
      },
      crosshair: {
        vertLine: { color: '#475569', labelBackgroundColor: '#1e293b' },
        horzLine: { color: '#475569', labelBackgroundColor: '#1e293b' },
      },
      timeScale: { borderColor: 'rgba(51, 65, 85, 0.4)', timeVisible: false, rightOffset: 5 },
      rightPriceScale: { borderColor: 'rgba(51, 65, 85, 0.4)' },
      width: chartRef.current.clientWidth,
      height: 500,
    });

    let series;
    if (chartType === 'candle') {
      series = chart.addSeries(CandlestickSeries, {
        upColor: '#22c55e', downColor: '#ef4444',
        borderUpColor: '#22c55e', borderDownColor: '#ef4444',
        wickUpColor: '#22c55e', wickDownColor: '#ef4444',
        lastValueVisible: true, priceLineVisible: true, priceLineStyle: LineStyle.Dotted,
      });
      const candleData = ohlcData
        .filter(d => d.open != null && d.high != null && d.low != null)
        .map(d => ({ time: d.date, open: d.open, high: d.high, low: d.low, close: d.close }));
      series.setData(candleData);
    } else {
      series = chart.addSeries(LineSeries, {
        lineWidth: 2, lastValueVisible: true, priceLineVisible: true,
        priceLineStyle: LineStyle.Dotted, crosshairMarkerRadius: 5,
      });
      // Build set of SHORT active dates for orange line coloring
      const shortDates = new Set();
      if (txnData) {
        let inShort = false;
        const sorted = [...txnData].sort((a, b) => a.date.localeCompare(b.date));
        for (const t of sorted) {
          if (t.type.toUpperCase() === 'SHORT SELL') inShort = true;
          if (t.type.toUpperCase().includes('SHORT COVER')) inShort = false;
          if (inShort) shortDates.add(t.date);
        }
        // Also mark dates between entry and exit
        for (const d of ohlcData) {
          if (inShort) shortDates.add(d.date);
          const txn = sorted.find(t => t.date === d.date);
          if (txn) {
            if (txn.type.toUpperCase() === 'SHORT SELL') inShort = true;
            if (txn.type.toUpperCase().includes('SHORT COVER')) inShort = false;
          }
        }
      }
      // Rebuild with proper short tracking
      const shortActive = new Set();
      if (txnData) {
        let active = false;
        for (const d of ohlcData) {
          const dayTxns = txnData.filter(t => t.date === d.date);
          for (const t of dayTxns) {
            if (t.type.toUpperCase() === 'SHORT SELL') active = true;
            if (t.type.toUpperCase().includes('SHORT COVER')) active = false;
          }
          if (active) shortActive.add(d.date);
        }
      }

      const priceData = ohlcData.map((d, i) => ({
        time: d.date,
        value: d.close,
        color: shortActive.has(d.date) ? SIGNAL_COLORS.SHORT_ACTIVE
          : i < ohlcData.length - 1 ? getZoneColor(ohlcData[i + 1].score) : getZoneColor(d.score),
      }));
      series.setData(priceData);
    }

    // Add signal markers from transactions
    if (txnData && txnData.length > 0 && showSignals) {
      const signals = buildSignals(txnData, showLongs, showShorts);
      signalsRef.current = signals;
      if (signals.length > 0) {
        // Sort by time (required by lightweight-charts) and set with fixed size
        const sorted = signals
          .sort((a, b) => a.time.localeCompare(b.time))
          .map(s => ({ ...s, size: 2 }));
        createSeriesMarkers(series, sorted, { zOrder: 'top' });
      }
    }

    chart.timeScale().fitContent();
    chartApiRef.current = chart;

    const handleResize = () => {
      if (chartRef.current) {
        const height = isFullscreen ? window.innerHeight - 120 : 500;
        chart.applyOptions({ width: chartRef.current.clientWidth, height });
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
      chartApiRef.current = null;
      markersPluginRef.current = null;
    };
  }, [ohlcData, txnData, chartType, showLongs, showShorts, showSignals, isFullscreen]);

  return (
    <Layout>
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <BarChart3 className="w-6 h-6 text-primary-400" />
          <h1 className="text-2xl font-bold text-white">Chart Simulator</h1>
        </div>
        <p className="text-dark-400 text-sm">Upload OHLC data and transactions to visualize trades on a chart. No server needed — everything runs in your browser.</p>
      </div>

      {/* File uploads */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <FileUpload
          label="OHLC Data"
          hint="CSV: date, open, high, low, close, score, short_signal"
          onData={handleOHLC}
          error={ohlcError}
        />
        <FileUpload
          label="Transactions"
          hint="CSV: trade_number, type, date, price, gain, days_held"
          onData={handleTxn}
          error={txnError}
        />
      </div>

      {/* Chart */}
      {ohlcData && (
        <div ref={chartContainerRef} className={`bg-dark-800 border border-dark-600 rounded-xl p-5 mb-6 ${isFullscreen ? 'fixed inset-0 z-50 rounded-none' : ''}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            {/* Legend */}
            <div className="flex flex-wrap items-center gap-3 text-xs text-dark-400">
              <span className="flex items-center gap-1.5"><span className="w-4 h-0.5 bg-green-500 inline-block rounded" /> Bullish</span>
              <span className="flex items-center gap-1.5"><span className="w-4 h-0.5 bg-red-500 inline-block rounded" /> Bearish</span>
              <span className="flex items-center gap-1.5"><span className="w-4 h-0.5 bg-gray-500 inline-block rounded" /> Neutral</span>
              <span className="text-dark-600">|</span>
              <span className="flex items-center gap-1.5"><span className="text-[#39ff14]">▲</span> Buy</span>
              <span className="flex items-center gap-1.5"><span className="text-[#ff2d55]">▼</span> Sell</span>
              <span className="flex items-center gap-1.5"><span className="text-[#ff8c1a]">▼</span> Short</span>
              <span className="flex items-center gap-1.5"><span className="text-[#fbbf24]">▲</span> Cover</span>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-dark-500">{ohlcData.length} pts</span>
              {txnData && <span className="text-xs text-dark-500">· {txnData.length} txns</span>}

              {/* Signals toggle */}
              <label className="flex items-center gap-1.5 cursor-pointer">
                <button onClick={() => { const n = !showSignals; showSignalsRef.current = n; setShowSignals(n); }}
                  className={`relative w-7 h-4 rounded-full transition-colors ${showSignals ? 'bg-green-500' : 'bg-dark-600'}`}>
                  <span className={`absolute top-[2px] h-3 w-3 rounded-full bg-white transition-transform ${showSignals ? 'left-[14px]' : 'left-[2px]'}`} />
                </button>
                <span className="text-xs text-dark-400">Signals</span>
              </label>

              {/* Longs toggle */}
              <label className="flex items-center gap-1.5 cursor-pointer">
                <button onClick={() => { const n = !showLongs; showLongsRef.current = n; setShowLongs(n); }}
                  className={`relative w-7 h-4 rounded-full transition-colors ${showLongs ? 'bg-emerald-500' : 'bg-dark-600'}`}>
                  <span className={`absolute top-[2px] h-3 w-3 rounded-full bg-white transition-transform ${showLongs ? 'left-[14px]' : 'left-[2px]'}`} />
                </button>
                <span className="text-xs text-dark-400">Longs</span>
              </label>

              {/* Shorts toggle */}
              {txnData && txnData.some(t => t.type.toUpperCase().includes('SHORT')) && (
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <button onClick={() => { const n = !showShorts; showShortsRef.current = n; setShowShorts(n); }}
                    className={`relative w-7 h-4 rounded-full transition-colors ${showShorts ? 'bg-orange-500' : 'bg-dark-600'}`}>
                    <span className={`absolute top-[2px] h-3 w-3 rounded-full bg-white transition-transform ${showShorts ? 'left-[14px]' : 'left-[2px]'}`} />
                  </button>
                  <span className="text-xs text-dark-400">Shorts</span>
                </label>
              )}

              {/* Chart type */}
              <button onClick={() => setChartType(chartType === 'line' ? 'candle' : 'line')}
                className="p-1.5 rounded text-dark-400 hover:text-white hover:bg-dark-600 transition-colors"
                title={chartType === 'line' ? 'Candlestick' : 'Line'}>
                {chartType === 'line' ? <MdCandlestickChart size={18} /> : <MdShowChart size={18} />}
              </button>

              {/* Reset zoom */}
              <button onClick={() => chartApiRef.current?.timeScale().fitContent()}
                className="p-1.5 rounded text-dark-400 hover:text-white hover:bg-dark-600 transition-colors" title="Reset zoom">
                <RotateCcw className="w-4 h-4" />
              </button>

              {/* Fullscreen */}
              <button onClick={toggleFullscreen}
                className="p-1.5 rounded text-dark-400 hover:text-white hover:bg-dark-600 transition-colors"
                title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
                {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div ref={chartRef} style={{ height: isFullscreen ? 'calc(100vh - 120px)' : 500 }} />
        </div>
      )}

      {/* Stats — matching All Indexes layout */}
      {stats && (
        <div className="bg-dark-800 border border-dark-600 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-primary-400" />
            <h3 className="text-lg font-semibold text-white">Trade Statistics</h3>
            <span className="text-xs text-dark-500">({stats.years} years · {stats.longTrades} longs · {stats.shortTrades} shorts)</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-600">
                  <th className="py-2 px-3 text-left text-dark-400 font-medium text-xs uppercase">CAGR</th>
                  <th className="py-2 px-3 text-center text-dark-400 font-medium text-xs uppercase">Trades/Yr</th>
                  <th className="py-2 px-3 text-center text-dark-400 font-medium text-xs uppercase">Avg Hold</th>
                  <th className="py-2 px-3 text-center text-dark-400 font-medium text-xs uppercase">% In Mkt</th>
                  <th className="py-2 px-3 text-center text-dark-400 font-medium text-xs uppercase">Win Rate</th>
                  <th className="py-2 px-3 text-center text-dark-400 font-medium text-xs uppercase">Avg Ret</th>
                  <th className="py-2 px-3 text-center text-dark-400 font-medium text-xs uppercase">Gain/Loss</th>
                  <th className="py-2 px-3 text-center text-dark-400 font-medium text-xs uppercase">Exp. Ratio</th>
                  <th className="py-2 px-3 text-center text-dark-400 font-medium text-xs uppercase">Best</th>
                  <th className="py-2 px-3 text-center text-dark-400 font-medium text-xs uppercase">Worst</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={`py-3 px-3 font-bold text-lg ${parseFloat(stats.cagr) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{stats.cagr}%</td>
                  <td className="py-3 px-3 text-center text-dark-300">{stats.tradesPerYear}</td>
                  <td className="py-3 px-3 text-center text-dark-300">{stats.avgDays}d</td>
                  <td className={`py-3 px-3 text-center font-medium ${stats.timeInMarket > 66.7 ? 'text-red-400' : 'text-teal-400'}`}>{stats.timeInMarket}%</td>
                  <td className={`py-3 px-3 text-center font-semibold ${parseFloat(stats.winRate) >= 50 ? 'text-green-400' : 'text-red-400'}`}>{stats.winRate}%</td>
                  <td className="py-3 px-3 text-center text-dark-300">{stats.avgReturn}%</td>
                  <td className="py-3 px-3 text-center">
                    <span className="text-green-400">{stats.avgGain}%</span>
                    <span className="text-dark-500 mx-1">/</span>
                    <span className="text-red-400">{stats.avgLoss}%</span>
                    <div className="text-yellow-400 text-xs">{stats.winLossRatio}x</div>
                  </td>
                  <td className={`py-3 px-3 text-center font-semibold ${parseFloat(stats.expRatio) >= 1 ? 'text-white' : 'text-red-400'}`}>{stats.expRatio}</td>
                  <td className="py-3 px-3 text-center text-green-400 font-semibold">{stats.bestGain}%</td>
                  <td className="py-3 px-3 text-center text-red-400 font-semibold">{stats.worstLoss}%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tabbed reports — Yearly Stats / Performance Summary / All Transactions */}
      {stats && txnData && (
        <div className="bg-dark-800 border border-dark-600 rounded-xl overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-dark-600">
            {[
              { id: 'yearly', label: 'Yearly Statistics', icon: '📊' },
              { id: 'performance', label: 'Performance Summary', icon: '📈' },
              { id: 'transactions', label: 'All Transactions', icon: '📋' },
            ].map(tab => (
              <button key={tab.id} onClick={() => setSimTab(tab.id)}
                className={`px-5 py-3 text-sm font-medium transition-all border-b-2 ${simTab === tab.id ? 'text-primary-400 border-primary-400 bg-dark-700/50' : 'text-dark-400 border-transparent hover:text-white hover:bg-dark-700/30'}`}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-5">
            {/* YEARLY STATISTICS */}
            {simTab === 'yearly' && yearlyStats.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-dark-600">
                      <th className="py-2.5 px-3 text-left text-dark-400 font-medium text-xs uppercase">Year</th>
                      <th className="py-2.5 px-3 text-center text-dark-400 font-medium text-xs uppercase">Trades</th>
                      <th className="py-2.5 px-3 text-center text-dark-400 font-medium text-xs uppercase">L / S</th>
                      <th className="py-2.5 px-3 text-right text-dark-400 font-medium text-xs uppercase">Strategy Return</th>
                      <th className="py-2.5 px-3 text-center text-dark-400 font-medium text-xs uppercase">Wins</th>
                      <th className="py-2.5 px-3 text-center text-dark-400 font-medium text-xs uppercase">Losses</th>
                      <th className="py-2.5 px-3 text-right text-dark-400 font-medium text-xs uppercase">Win Rate</th>
                      <th className="py-2.5 px-3 text-right text-dark-400 font-medium text-xs uppercase">% In Mkt</th>
                      <th className="py-2.5 px-3 text-right text-dark-400 font-medium text-xs uppercase">Avg MAE</th>
                      <th className="py-2.5 px-3 text-right text-dark-400 font-medium text-xs uppercase">Max MAE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {yearlyStats.map((y, idx) => (
                      <tr key={y.year} className={`border-t border-dark-700/50 hover:bg-dark-700/30 cursor-pointer ${idx % 2 === 0 ? '' : 'bg-dark-900/20'}`}
                        onClick={() => { setSelectedYear(String(y.year)); setSimTab('transactions'); }}>
                        <td className="py-2.5 px-3 text-white font-medium">{y.year}</td>
                        <td className="py-2.5 px-3 text-center text-white">{y.total_trades}</td>
                        <td className="py-2.5 px-3 text-center">
                          <span className="text-emerald-400">{y.long_trades}</span>
                          <span className="text-dark-600 mx-1">/</span>
                          <span className="text-orange-400">{y.short_trades}</span>
                        </td>
                        <td className={`py-2.5 px-3 text-right font-semibold ${parseFloat(y.strategy_return) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {parseFloat(y.strategy_return) > 0 ? '+' : ''}{y.strategy_return}%
                        </td>
                        <td className="py-2.5 px-3 text-center text-green-400">{y.winning_trades}</td>
                        <td className="py-2.5 px-3 text-center text-red-400">{y.losing_trades}</td>
                        <td className={`py-2.5 px-3 text-right ${parseFloat(y.win_rate) >= 50 ? 'text-green-400' : 'text-red-400'}`}>{y.win_rate}%</td>
                        <td className="py-2.5 px-3 text-right text-teal-400">{y.time_in_market}%</td>
                        <td className="py-2.5 px-3 text-right text-orange-400">{y.avg_mae ?? '-'}%</td>
                        <td className="py-2.5 px-3 text-right text-orange-400">{y.max_mae ?? '-'}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* PERFORMANCE SUMMARY */}
            {simTab === 'performance' && stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-dark-700/50 rounded-lg p-4">
                  <div className="text-xs text-dark-400 uppercase mb-1">Strategy CAGR</div>
                  <div className={`text-2xl font-bold ${parseFloat(stats.cagr) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{stats.cagr}%</div>
                  <div className="text-xs text-dark-500">{stats.years} years</div>
                </div>
                <div className="bg-dark-700/50 rounded-lg p-4">
                  <div className="text-xs text-dark-400 uppercase mb-1">Total Trades</div>
                  <div className="text-2xl font-bold text-white">{stats.totalTrades}</div>
                  <div className="text-xs text-dark-500">{stats.longTrades}L / {stats.shortTrades}S</div>
                </div>
                <div className="bg-dark-700/50 rounded-lg p-4">
                  <div className="text-xs text-dark-400 uppercase mb-1">Win Rate</div>
                  <div className={`text-2xl font-bold ${parseFloat(stats.winRate) >= 50 ? 'text-green-400' : 'text-red-400'}`}>{stats.winRate}%</div>
                  <div className="text-xs text-dark-500">{stats.wins}W / {stats.losses}L</div>
                </div>
                <div className="bg-dark-700/50 rounded-lg p-4">
                  <div className="text-xs text-dark-400 uppercase mb-1">Trades / Year</div>
                  <div className="text-2xl font-bold text-primary-400">{stats.tradesPerYear}</div>
                  <div className="text-xs text-dark-500">avg {stats.avgDays}d held</div>
                </div>
                <div className="bg-dark-700/50 rounded-lg p-4">
                  <div className="text-xs text-dark-400 uppercase mb-1">Avg Return</div>
                  <div className={`text-2xl font-bold ${parseFloat(stats.avgReturn) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{stats.avgReturn}%</div>
                </div>
                <div className="bg-dark-700/50 rounded-lg p-4">
                  <div className="text-xs text-dark-400 uppercase mb-1">Gain / Loss</div>
                  <div className="text-lg font-bold">
                    <span className="text-green-400">{stats.avgGain}%</span>
                    <span className="text-dark-500 mx-1">/</span>
                    <span className="text-red-400">{stats.avgLoss}%</span>
                  </div>
                  <div className="text-xs text-yellow-400">{stats.winLossRatio}x ratio</div>
                </div>
                <div className="bg-dark-700/50 rounded-lg p-4">
                  <div className="text-xs text-dark-400 uppercase mb-1">% Time in Market</div>
                  <div className={`text-2xl font-bold ${stats.timeInMarket > 67 ? 'text-red-400' : 'text-teal-400'}`}>{stats.timeInMarket}%</div>
                </div>
                <div className="bg-dark-700/50 rounded-lg p-4">
                  <div className="text-xs text-dark-400 uppercase mb-1">Best / Worst</div>
                  <div className="text-lg font-bold">
                    <span className="text-green-400">{stats.bestGain}%</span>
                    <span className="text-dark-500 mx-1">/</span>
                    <span className="text-red-400">{stats.worstLoss}%</span>
                  </div>
                </div>
              </div>
            )}

            {/* ALL TRANSACTIONS — matching Individual Indexes layout */}
            {simTab === 'transactions' && txnData && (() => {
              // Pair entries with exits
              const sorted = [...txnData].sort((a, b) => a.date.localeCompare(b.date));
              const entryTypes = ['BUY', 'SHORT SELL'];
              const exitTypes = ['SELL', 'SELL (FORCED)', 'SHORT COVER', 'SHORT COVER (FORCED)'];
              const pairs = [];
              let tradeNum = 0;
              for (let i = 0; i < sorted.length; i++) {
                const t = sorted[i];
                const typeUp = t.type.toUpperCase().trim();
                if (entryTypes.includes(typeUp)) {
                  tradeNum++;
                  const isShort = typeUp === 'SHORT SELL';
                  // Find matching exit
                  const exitIdx = sorted.findIndex((s, j) => j > i && exitTypes.includes(s.type.toUpperCase().trim()));
                  const exit = exitIdx >= 0 ? sorted[exitIdx] : null;
                  pairs.push({
                    trade_number: tradeNum,
                    direction: isShort ? 'SHORT' : 'LONG',
                    entry_date: t.date,
                    exit_date: exit?.date || null,
                    entry_price: t.price,
                    exit_price: exit?.price || null,
                    gain: exit?.gain ?? null,
                    days_held: exit?.days_held ?? null,
                    mae: exit?.mae ?? null,
                    exit_type: exit?.type || null,
                  });
                  if (exitIdx >= 0) i = exitIdx; // skip to after exit
                }
              }

              // Filters
              const filterOpts = [
                { key: 'all', label: 'All', color: 'bg-dark-600' },
                { key: 'losses', label: 'Losses', color: 'bg-red-600' },
                { key: 'top10losses', label: 'Top 10 Losses', color: 'bg-red-600' },
                { key: 'top10profits', label: 'Top 10 Profits', color: 'bg-green-600' },
              ];

              let filtered = [...pairs].reverse(); // newest first
              if (selectedYear) filtered = filtered.filter(t => t.entry_date?.startsWith(selectedYear) || t.exit_date?.startsWith(selectedYear));
              if (simFilterType === 'losses') filtered = filtered.filter(t => t.gain != null && t.gain <= 0);
              else if (simFilterType === 'top10losses') filtered = [...filtered].filter(t => t.gain != null).sort((a, b) => a.gain - b.gain).slice(0, 10);
              else if (simFilterType === 'top10profits') filtered = [...filtered].filter(t => t.gain != null).sort((a, b) => b.gain - a.gain).slice(0, 10);

              const completedTrades = filtered.filter(t => t.gain != null).length;
              const totalGain = filtered.reduce((sum, t) => sum + (t.gain ?? 0), 0);
              const years = [...new Set(pairs.map(t => t.entry_date?.substring(0, 4)).filter(Boolean))].sort((a, b) => b - a);
              const hasShorts = pairs.some(t => t.direction === 'SHORT');

              return (
                <div>
                  {/* Header */}
                  <h3 className="text-lg font-semibold text-white mb-4">
                    Transaction History <span className="text-dark-400 font-normal text-sm">
                      ({completedTrades} trades{hasShorts ? ` · ${pairs.filter(t => t.direction === 'LONG').length}L / ${pairs.filter(t => t.direction === 'SHORT').length}S` : ''})
                    </span>
                  </h3>

                  {/* Filters */}
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-5 pb-4 border-b border-dark-700">
                    <div className="flex items-center space-x-1 bg-dark-700 rounded-lg p-1">
                      {filterOpts.map(f => (
                        <button key={f.key} onClick={() => setSimFilterType(f.key)}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${simFilterType === f.key ? `${f.color} text-white` : 'text-dark-400 hover:text-dark-200'}`}>{f.label}</button>
                      ))}
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1 bg-dark-700 rounded-lg p-1">
                        <span className="text-xs text-dark-400 px-2">Year:</span>
                        <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)}
                          className={`bg-dark-600 border-none text-xs rounded px-2 py-1.5 focus:outline-none cursor-pointer ${selectedYear ? 'text-primary-400 font-medium' : 'text-dark-300'}`}>
                          <option value="">All</option>
                          {years.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                      </div>
                      {selectedYear && <button onClick={() => setSelectedYear('')} className="text-xs text-dark-400 hover:text-red-400">Clear</button>}
                    </div>
                  </div>

                  {/* Total Gains */}
                  <div className="mb-5">
                    <div className="bg-dark-700/50 rounded-lg p-4 border border-dark-600 inline-block">
                      <p className="text-dark-400 text-xs uppercase tracking-wider">Total % Gains</p>
                      <p className={`text-2xl font-bold mt-1 ${totalGain >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {totalGain > 0 ? '+' : ''}{totalGain.toFixed(2)}%
                      </p>
                      <p className="text-dark-500 text-xs mt-0.5">{completedTrades} trades</p>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                    <table className="w-full">
                      <thead className="sticky top-0 bg-dark-800">
                        <tr className="text-dark-400 text-xs uppercase tracking-wider">
                          <th className="text-left py-3 px-2 font-medium">Trade #</th>
                          {hasShorts && <th className="text-left py-3 px-2 font-medium">Direction</th>}
                          <th className="text-left py-3 px-2 font-medium">Entry Date</th>
                          <th className="text-left py-3 px-2 font-medium">Exit Date</th>
                          <th className="text-right py-3 px-2 font-medium">Entry Price</th>
                          <th className="text-right py-3 px-2 font-medium">Exit Price</th>
                          <th className="text-right py-3 px-2 font-medium">Gain %</th>
                          <th className="text-right py-3 px-2 font-medium">Days Held</th>
                          <th className="text-right py-3 px-2 font-medium">MAE %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((t, idx) => (
                          <tr key={idx} className={`border-t border-dark-700/50 hover:bg-dark-800/50 ${t.gain != null && t.gain < 0 && idx > 0 && filtered[idx-1].gain != null && filtered[idx-1].gain < 0 ? 'bg-red-900/20' : ''}`}>
                            <td className="py-3 px-2 text-white">{t.trade_number}</td>
                            {hasShorts && <td className="py-3 px-2">
                              {t.direction === 'SHORT'
                                ? <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-orange-600 text-white">↘ SHORT</span>
                                : <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-600 text-white">↗ LONG</span>}
                            </td>}
                            <td className="py-3 px-2 text-dark-200">{t.entry_date}</td>
                            <td className="py-3 px-2 text-dark-200">
                              {t.exit_date ? (t.exit_type?.toUpperCase().includes('FORCED') ? <>{t.exit_date} <span className="text-xs text-amber-400">(Close)</span></> : t.exit_date) : <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-900/40 text-emerald-400 border border-emerald-700/50">OPEN</span>}
                            </td>
                            <td className="py-3 px-2 text-right text-dark-200">{t.entry_price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="py-3 px-2 text-right text-dark-200">{t.exit_price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '-'}</td>
                            <td className={`py-3 px-2 text-right font-medium ${t.gain == null ? 'text-dark-400' : t.gain > 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {t.gain != null ? `${t.gain > 0 ? '+' : ''}${t.gain.toFixed(2)}%` : '-'}
                            </td>
                            <td className="py-3 px-2 text-right text-dark-300">{t.days_held ?? '-'}</td>
                            <td className={`py-3 px-2 text-right ${t.mae != null ? 'text-orange-400' : 'text-dark-400'}`}>{t.mae != null ? `${t.mae.toFixed(2)}%` : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!ohlcData && !txnData && (
        <div className="text-center py-16">
          <BarChart3 className="w-12 h-12 text-dark-600 mx-auto mb-4" />
          <h3 className="text-dark-300 text-lg font-semibold mb-2">Upload data to get started</h3>
          <p className="text-dark-500 text-sm max-w-md mx-auto">
            Upload an OHLC CSV file to see the price chart. Optionally add a transactions file to overlay buy/sell/short/cover signals and see trade statistics.
          </p>
        </div>
      )}
    </Layout>
  );
}
